import React from 'react';
import { useTheme } from 'react-jss';
import Select, { OptionTypeBase, Theme as SelectTheme } from 'react-select';
import color from 'color';
import BaseInput, { FocusEvent } from './BaseInput';
import { Theme } from '../_lib/theme';
import { getColorCodeRGBA } from '../_lib/helpers/colors';

const useSelectStyles = (theme: Theme) => {
  const fontStyle = {
    fontSize: '16px',
    fontWeight: 700,
    fontFamily: theme.typography.fontFamily,
    textOverflow: 'ellipsis',
  };

  return {
    container: (provided: OptionTypeBase) => ({
      ...provided,
      padding: '0px !important',
    }),
    control: (provided: OptionTypeBase) => ({
      ...provided,
      border: 'none !important',
      '& > div': {
        padding: '25px 15px 9px 15px',
      },
    }),
    input: (provided: OptionTypeBase) => ({
      ...provided,
      margin: 0,
      padding: 0,
      input: fontStyle,
    }),
    valueContainer: (provided: OptionTypeBase) => ({
      ...provided,
      ...fontStyle,
    }),
    singleValue: () =>
      ({
        position: 'static',
        transform: 'none',
        WebkitTransform: 'none',
      } as React.CSSProperties),
    multiValue: (provided: OptionTypeBase) => ({
      ...provided,
      margin: '0 3px 0 0',
      background: '#e2e2e4',
      overflow: 'hidden',
    }),
    multiValueLabel: (provided: OptionTypeBase) => ({
      ...provided,
      fontSize: 12,
      padding: '3px',
    }),
    indicatorsContainer: (provided: OptionTypeBase) => ({
      ...provided,
      padding: '0 5px 0 0 !important',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    menu: (provided: OptionTypeBase, props: { selectProps: Props }) =>
      ({
        ...provided,
        top: props.selectProps.openDirection === 'up' && 'auto',
        bottom: props.selectProps.openDirection === 'up' && '100%',
      } as React.CSSProperties),
  };
};

const useSelectTheme = (theme: Theme) => (selectTheme: SelectTheme) => ({
  ...selectTheme,
  colors: {
    ...selectTheme.colors,
    primary: theme.colors.primary,
    primary75: getColorCodeRGBA(color(theme.colors.primary).alpha(0.75)),
    primary50: getColorCodeRGBA(color(theme.colors.primary).alpha(0.5)),
    primary25: getColorCodeRGBA(color(theme.colors.primary).alpha(0.3)),
  },
});

type Props = {
  title?: string;
  name?: string;
  value?: any;
  error?: string;
  onChange?: (value: any) => void;
  onFocus?: (e: FocusEvent) => void;
  onBlur?: (e: FocusEvent) => void;
  options: { value: string; label: string }[];

  isMulti?: boolean;
  autoFocus?: boolean;
  autoComplete?: boolean;
  openDirection?: 'up' | 'down';
  testId?: string;
};

const TextField = (props: Props) => {
  const {
    title,
    name,
    value,
    error,
    onChange = () => {},
    onFocus = () => {},
    onBlur = () => {},
    options = [],

    isMulti = false,
    autoFocus = false,
    autoComplete = false,
    openDirection,
    testId,
  } = props;

  const theme = (useTheme() as any) as Theme;
  const selectStyles = useSelectStyles(theme);
  const selectTheme = useSelectTheme(theme);

  return (
    <BaseInput
      title={title}
      value={value}
      error={error}
      onFocus={onFocus}
      onBlur={onBlur}
      testId={testId}
    >
      {baseProps => (
        <Select
          name={name}
          value={value}
          onChange={onChange}
          onFocus={baseProps.onFocus}
          onBlur={baseProps.onBlur}
          autoFocus={autoFocus}
          autoComplete={autoComplete ? 'true' : ''}
          options={options}
          isMulti={isMulti}
          placeholder={false}
          styles={selectStyles}
          theme={selectTheme}
          openDirection={openDirection}
        />
      )}
    </BaseInput>
  );
};

export default TextField;
